import type { ReactNode } from 'react';
import Image from '@/components/ui/Image';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { INVESTMENT_ROLE_LABELS, MIRROR_NOTE, institutionLabel } from '@/lib/investments/sources';
import { cn } from '@/lib/utils';
import type { InvestmentSource } from '@/types';

function InstitutionAvatar({
  institution,
  imageUrl,
  className,
}: {
  institution: string | null;
  imageUrl: string | null;
  className?: string;
}) {
  if (imageUrl) {
    return (
      <Image
        src={imageUrl}
        alt=""
        width={20}
        height={20}
        className={cn('size-5 shrink-0 rounded-full object-cover', className)}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'grid size-5 shrink-0 place-items-center rounded-full bg-muted text-3xs font-bold text-text-secondary',
        className,
      )}
    >
      {institutionLabel(institution).charAt(0).toUpperCase()}
    </span>
  );
}

export function InvestmentSourceCluster({ sources }: { sources: InvestmentSource[] }) {
  if (sources.length < 2) return null;

  const shown = sources.slice(0, 3);

  return (
    <span className="inline-flex shrink-0 items-center gap-1" aria-hidden>
      <span className="flex items-center -space-x-1.5">
        {shown.map((source) => (
          <InstitutionAvatar
            key={source.investmentId}
            institution={source.institution}
            imageUrl={source.institutionImage}
            className="size-4 ring-1 ring-surface"
          />
        ))}
      </span>
      <span className="rounded bg-muted px-1 font-mono text-3xs font-semibold text-text-secondary tabular-nums">
        {sources.length}
      </span>
    </span>
  );
}

export function InvestmentSourceList({
  sources,
  renderAction,
}: {
  sources: InvestmentSource[];
  /** Optional per-source affordance (e.g. detaching a source from the group). */
  renderAction?: (source: InvestmentSource) => ReactNode;
}) {
  const formatCurrency = useFormatCurrency();

  if (sources.length < 2) return null;

  const hasMirror = sources.some((source) => source.role === 'MIRROR');

  return (
    <div>
      {/* A grid, not a flex row: the role pills differ in width and only some sources can be
          detached, so laying this out inline let every row put its money in a different place.
          Fixed columns keep the amounts in one right-aligned stack, which is the whole point of
          showing them together. */}
      <ul className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto] items-center gap-x-1.5 gap-y-1.5">
        {sources.map((source) => {
          const isMirror = source.role === 'MIRROR';
          return (
            <li key={source.investmentId} className="col-span-full grid grid-cols-subgrid items-center">
              <InstitutionAvatar institution={source.institution} imageUrl={source.institutionImage} />
              <span
                className={cn(
                  'truncate text-xs',
                  isMirror ? 'text-text-tertiary' : 'text-text-secondary',
                )}
              >
                {institutionLabel(source.institution)}
              </span>
              <span
                className={cn(
                  'w-16 rounded px-1.5 py-0.5 text-center text-3xs font-semibold',
                  isMirror ? 'bg-muted text-text-tertiary' : 'bg-brand-50 text-brand-700',
                )}
              >
                {INVESTMENT_ROLE_LABELS[source.role]}
              </span>
              <span
                className={cn(
                  'justify-self-end font-mono text-xs tabular-nums',
                  isMirror ? 'font-normal text-text-tertiary' : 'font-bold text-text-primary',
                )}
              >
                {formatCurrency(source.balance)}
              </span>
              {/* Always rendered, even when the source cannot be detached — an absent cell
                  would drag this row's amount out of line with its siblings'. */}
              <span className="justify-self-end text-right empty:hidden">{renderAction?.(source)}</span>
            </li>
          );
        })}
      </ul>
      {hasMirror && <p className="mt-2 text-2xs text-text-tertiary">{MIRROR_NOTE}</p>}
    </div>
  );
}
